'use client';

import { useEffect } from 'react';
import {
  useFacebookSDK,
  preloadFacebookResources,
} from '@/lib/facebook-sdk-optimized';

interface FacebookOptimizerProps {
  appId?: string;
  version?: string;
  lazy?: boolean;
  preload?: boolean;
}

export default function FacebookOptimizer({
  appId,
  version = 'v18.0',
  lazy = true,
  preload = true,
}: FacebookOptimizerProps) {
  const { loadFacebookSDK } = useFacebookSDK({
    appId,
    version,
    lazy,
  });

  useEffect(() => {
    if (preload) {
      // Preload Facebook resources for better caching
      preloadFacebookResources();
    }
  }, [preload]);

  useEffect(() => {
    // Only load Facebook SDK when actually needed
    if (lazy) {
      // Check if user is likely to interact with Facebook features
      const shouldLoadFacebook = () => {
        // Load on user interaction or after delay
        const timer = setTimeout(() => {
          loadFacebookSDK().catch(console.error);
        }, 3000); // Load after 3 seconds

        // Load on user interaction
        const handleInteraction = () => {
          clearTimeout(timer);
          loadFacebookSDK().catch(console.error);
          document.removeEventListener('click', handleInteraction);
          document.removeEventListener('scroll', handleInteraction);
          document.removeEventListener('touchstart', handleInteraction);
        };

        document.addEventListener('click', handleInteraction, {
          passive: true,
        });
        document.addEventListener('scroll', handleInteraction, {
          passive: true,
        });
        document.addEventListener('touchstart', handleInteraction, {
          passive: true,
        });

        return () => {
          clearTimeout(timer);
          document.removeEventListener('click', handleInteraction);
          document.removeEventListener('scroll', handleInteraction);
          document.removeEventListener('touchstart', handleInteraction);
        };
      };

      return shouldLoadFacebook();
    }
  }, [lazy, loadFacebookSDK]);

  // Return null as this is just an optimizer component
  return null;
}
